import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login:    (data) => api.post('/api/v1/auth/login', data),
  register: (data) => api.post('/api/v1/auth/register', data),
};

export const customerAPI = {
  getAll:     ()         => api.get('/api/v1/customers'),
  getById:    (id)       => api.get(`/api/v1/customers/${id}`),
  getByCode:  (code)     => api.get(`/api/v1/customers/code/${code}`),
  create:     (data)     => api.post('/api/v1/customers', data),
  update:     (id, data) => api.put(`/api/v1/customers/${id}`, data),
  deactivate: (id)       => api.delete(`/api/v1/customers/${id}`),
};

export const productAPI = {
  getAll:             ()           => api.get('/api/v1/products'),
  getById:            (id)         => api.get(`/api/v1/products/${id}`),
  getByCode:          (code)       => api.get(`/api/v1/products/code/${code}`),
  getByCustomer:      (customerId) => api.get(`/api/v1/products/customer/${customerId}`),
  getPendingSort:     ()           => api.get('/api/v1/products/pending-sort'),
  getUnpacked:        ()           => api.get('/api/v1/products/unpacked'),
  receive:            (data)       => api.post('/api/v1/products/receive', data),
  sort:               (id, data)   => api.put(`/api/v1/products/${id}/sort`, data),
  pack:               (id, data)   => api.post(`/api/v1/products/${id}/pack`, data),
  getCartonContents:  (cartonId)   => api.get(`/api/v1/products/carton/${cartonId}/contents`),
  getCartonsByProduct:(id)         => api.get(`/api/v1/products/${id}/cartons`),
};

export const warehouseAPI = {
  getAll:             ()           => api.get('/api/v1/warehouse/cartons'),
  getById:            (id)         => api.get(`/api/v1/warehouse/cartons/${id}`),
  getByGrn:           (grn)        => api.get(`/api/v1/warehouse/cartons/grn/${grn}`),
  getByCustomer:      (customerId) => api.get(`/api/v1/warehouse/cartons/customer/${customerId}`),
  getPendingSorting:  ()           => api.get('/api/v1/warehouse/cartons/pending-sorting'),
  receive:            (data)       => api.post('/api/v1/warehouse/cartons/receive', data),
  update:             (id, data)   => api.put(`/api/v1/warehouse/cartons/${id}`, data),
  generateBarcode:    (id)         => api.post(`/api/v1/warehouse/cartons/${id}/barcode`),
  scanBarcode:        (barcode)    => api.post(`/api/v1/warehouse/cartons/scan/${barcode}`),
  // BD receiving — full scan with products list + photos
  bdScan:             (barcode)    => api.post(`/api/v1/warehouse/cartons/bd-scan/${barcode}`),
  uploadPhoto:        (id, data)   => api.post(`/api/v1/warehouse/cartons/${id}/photos`, data),
  getPhotos:          (id)         => api.get(`/api/v1/warehouse/cartons/${id}/photos`),
  deletePhoto:        (photoId)    => api.delete(`/api/v1/warehouse/photos/${photoId}`),
  markDelivered:      (id)         => api.post(`/api/v1/warehouse/cartons/${id}/deliver`),
};

export const shipmentAPI = {
  getAll:       ()               => api.get('/api/v1/shipments'),
  getById:      (id)             => api.get(`/api/v1/shipments/${id}`),
  getByNumber:  (num)            => api.get(`/api/v1/shipments/number/${num}`),
  create:       (data)           => api.post('/api/v1/shipments', data),
  addCartons:   (id, data)       => api.post(`/api/v1/shipments/${id}/cartons`, data),
  dispatch:     (id, data)       => api.post(`/api/v1/shipments/${id}/dispatch`, data),
  markInTransit:(id, location)   => api.post(`/api/v1/shipments/${id}/in-transit?location=${encodeURIComponent(location || '')}`), // NEW
  markArrived:  (id)             => api.post(`/api/v1/shipments/${id}/arrived`),
};

export const trackingAPI = {
  addEvent:          (data)    => api.post('/api/v1/tracking/events', data),
  getByCarton:       (id)      => api.get(`/api/v1/tracking/carton/${id}`),
  getByShipment:     (id)      => api.get(`/api/v1/tracking/shipment/${id}`),
  trackPublic:       (barcode) => api.get(`/api/v1/tracking/public/barcode/${barcode}`),    // returns TrackingTimelineResponse
  getTimelineByCarton:(id)     => api.get(`/api/v1/tracking/timeline/carton/${id}`),        // NEW
};

export const reportAPI = {
  getDashboard: () => api.get('/api/v1/reports/dashboard'),
};

export const barcodeAPI = {
  generate: (content) => api.get(`/api/v1/barcodes/generate?content=${content}`),
};

export default api;
